import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { useReports } from '../../hooks/useReports';
import { useProjects } from '../../hooks/useProjects';
import { 
  X, 
  FileText, 
  Calendar, 
  Users, 
  BarChart3, 
  TrendingUp, 
  Target,
  Globe,
  Mail,
  Smartphone,
  ShoppingCart,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Loader,
  Edit,
  Save,
  Play,
  Clock
} from 'lucide-react';

interface EditReportModalProps {
  isOpen: boolean;
  report: any | null;
  onClose: () => void;
  onUpdated?: () => void;
}

export function EditReportModal({ isOpen, report, onClose, onUpdated }: EditReportModalProps) {
  const [step, setStep] = useState(1);
  const [reportData, setReportData] = useState({
    name: '',
    description: '',
    report_type: 'custom' as 'custom' | 'analytics' | 'performance' | 'conversion' | 'audience' | 'seo' | 'social',
    config: {
      widgets: [] as any[],
      metrics: [] as string[],
      dateRange: { type: 'last_30_days' }
    },
    schedule: null as string | null,
    is_scheduled: false
  });

  const [isGenerating, setIsGenerating] = useState(false);

  const { updateReport, generateReport, isUpdating } = useReports();
  const { projects } = useProjects();

  // Initialize form data when report changes
  useEffect(() => {
    if (report) {
      setReportData({
        name: report.name || '',
        description: report.description || '',
        report_type: report.report_type || 'custom',
        config: {
          widgets: report.config?.widgets || [],
          metrics: report.config?.metrics || [],
          dateRange: report.config?.dateRange || { type: 'last_30_days' }
        },
        schedule: report.schedule || null,
        is_scheduled: report.is_scheduled || false
      });
    }
  }, [report]);

  const availableWidgets = [
    { id: 'traffic-sources', name: 'Traffic Sources', icon: Globe, type: 'chart', dataSource: 'google_analytics' },
    { id: 'conversion-rates', name: 'Conversion Rates', icon: Target, type: 'metric', dataSource: 'google_analytics' },
    { id: 'page-views', name: 'Page Views', icon: FileText, type: 'chart', dataSource: 'google_analytics' },
    { id: 'unique-visitors', name: 'Unique Visitors', icon: Users, type: 'metric', dataSource: 'google_analytics' },
    { id: 'bounce-rate', name: 'Bounce Rate', icon: TrendingUp, type: 'chart', dataSource: 'google_analytics' },
    { id: 'session-duration', name: 'Session Duration', icon: Clock, type: 'metric', dataSource: 'google_analytics' },
    { id: 'roi-analysis', name: 'ROI Analysis', icon: BarChart3, type: 'metric', dataSource: 'google_analytics' },
    { id: 'sessions-over-time', name: 'Sessions Over Time', icon: TrendingUp, type: 'chart', dataSource: 'google_analytics' },
    { id: 'device-categories', name: 'Device Categories', icon: Smartphone, type: 'chart', dataSource: 'google_analytics' },
    { id: 'top-pages', name: 'Top Pages', icon: FileText, type: 'table', dataSource: 'google_analytics' },
    { id: 'keyword-rankings', name: 'Keyword Rankings', icon: TrendingUp, type: 'table', dataSource: 'search_console' },
    { id: 'social-engagement', name: 'Social Engagement', icon: Users, type: 'metric', dataSource: 'facebook' },
    { id: 'email-performance', name: 'Email Performance', icon: Mail, type: 'chart', dataSource: 'mailchimp' },
    { id: 'ad-performance', name: 'Ad Performance', icon: Target, type: 'table', dataSource: 'google_ads' }
  ];

  const availableMetrics = [
    'totalSessions',
    'totalUsers',
    'totalConversions',
    'bounceRate',
    'avgSessionDuration',
    'pageviews',
    'conversionRate'
  ];

  const scheduleOptions = [
    { value: null, label: 'No Schedule' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleWidgetToggle = (widget: any) => {
    setReportData(prev => {
      const exists = prev.config.widgets.find((w: any) => w.id === widget.id);
      const newWidgets = exists
        ? prev.config.widgets.filter((w: any) => w.id !== widget.id)
        : [...prev.config.widgets, {
            id: widget.id,
            type: widget.type,
            title: widget.name,
            dataSource: widget.dataSource
          }];

      return {
        ...prev,
        config: {
          ...prev.config,
          widgets: newWidgets
        }
      };
    });
  };

  const handleMetricToggle = (metric: string) => {
    setReportData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        metrics: prev.config.metrics.includes(metric)
          ? prev.config.metrics.filter(m => m !== metric)
          : [...prev.config.metrics, metric]
      }
    }));
  };

  const handleUpdate = async () => {
    if (!report) return;

    try {
      const updateData = {
        name: reportData.name,
        description: reportData.description,
        config: reportData.config,
        schedule: reportData.schedule || null
      };
      
      await updateReport({
        reportId: report.id,
        data: updateData
      });

      onUpdated?.();
      onClose();
    } catch (error) {
      console.error('Failed to update report:', error);
    }
  };

  const handleUpdateAndGenerate = async () => {
    if (!report) return;

    try {
      setIsGenerating(true);
      
      // First update the report
      const updateData = {
        name: reportData.name,
        description: reportData.description,
        config: reportData.config,
        schedule: reportData.schedule || null
      };
      
      await updateReport({
        reportId: report.id,
        data: updateData
      });

      // Then generate the report
      await generateReport(report.id);

      onUpdated?.();
      onClose();
    } catch (error) {
      console.error('Failed to update and generate report:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen || !report) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <div className="p-6 flex-1 overflow-y-auto min-h-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Edit Report</h2>
              <p className="text-gray-600 dark:text-gray-400">Step {step} of 3</p>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Progress</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{Math.round((step / 3) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 3) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Step Content */}
          <div className="pb-6">
            {/* Step 1: Basic Information */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Report Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="report-name" className="text-gray-700 dark:text-gray-300">Report Name</Label>
                      <Input
                        id="report-name"
                        placeholder="Enter report name"
                        value={reportData.name}
                        onChange={(e) => setReportData({ ...reportData, name: e.target.value })}
                        className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="report-description" className="text-gray-700 dark:text-gray-300">Description</Label>
                      <Input
                        id="report-description"
                        placeholder="Describe what this report tracks"
                        value={reportData.description}
                        onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                        className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>

                    <div>
                      <Label className="text-gray-700 dark:text-gray-300">Report Type</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                        {[
                          { value: 'custom', label: 'Custom', icon: FileText },
                          { value: 'analytics', label: 'Analytics', icon: BarChart3 },
                          { value: 'performance', label: 'Performance', icon: TrendingUp }
                        ].map((type) => (
                          <div
                            key={type.value}
                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                              reportData.report_type === type.value
                                ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                            }`}
                            onClick={() => setReportData({ ...reportData, report_type: type.value as any })}
                          >
                            <div className="text-center">
                              <type.icon className={`w-5 h-5 mx-auto mb-2 ${
                                reportData.report_type === type.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                              }`} />
                              <span className={`text-sm font-medium ${
                                reportData.report_type === type.value ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-white'
                              }`}>
                                {type.label}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Configure Widgets */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Configure Widgets</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                    {availableWidgets.map((widget) => {
                      const isSelected = reportData.config.widgets.some((w: any) => w.id === widget.id);
                      
                      return (
                        <div
                          key={widget.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                          }`}
                          onClick={() => handleWidgetToggle(widget)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <widget.icon className={`w-5 h-5 ${
                                isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                              }`} />
                              <div>
                                <h4 className={`font-medium ${
                                  isSelected ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-white'
                                }`}>
                                  {widget.name}
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{widget.type}</p>
                              </div>
                            </div>
                            {isSelected && <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-gray-700 dark:text-gray-300">Key Metrics</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                    {availableMetrics.map((metric) => {
                      const isSelected = reportData.config.metrics.includes(metric);
                      
                      return (
                        <div
                          key={metric}
                          className={`p-2 border rounded-lg cursor-pointer transition-colors text-center ${
                            isSelected
                              ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                          }`}
                          onClick={() => handleMetricToggle(metric)}
                        >
                          <span className={`text-sm ${
                            isSelected ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-white'
                          }`}>
                            {metric}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Schedule */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Schedule Settings</h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {scheduleOptions.map((option) => (
                      <div
                        key={option.value || 'none'}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          reportData.schedule === option.value
                            ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                        }`}
                        onClick={() => setReportData({ ...reportData, schedule: option.value })}
                      >
                        <div className="text-center">
                          <Calendar className={`w-5 h-5 mx-auto mb-2 ${
                            reportData.schedule === option.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                          }`} />
                          <span className={`text-sm font-medium ${
                            reportData.schedule === option.value ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-white'
                          }`}>
                            {option.label}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Report Summary</h4>
                  <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <p><span className="font-medium">Name:</span> {reportData.name}</p>
                    <p><span className="font-medium">Type:</span> {reportData.report_type}</p>
                    <p><span className="font-medium">Widgets:</span> {reportData.config.widgets.length} selected</p>
                    <p><span className="font-medium">Metrics:</span> {reportData.config.metrics.length} selected</p>
                    <p><span className="font-medium">Schedule:</span> {reportData.schedule ? reportData.schedule : 'One-time'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
          <div className="flex space-x-3">
            {step > 1 && (
              <Button variant="outline" onClick={handleBack} className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                Back
              </Button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <Button variant="outline" onClick={onClose} className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              Cancel
            </Button>
            
            {step < 3 ? (
              <Button 
                onClick={handleNext}
                disabled={step === 1 && (!reportData.name || !reportData.description)}
                className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white"
              >
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <div className="flex space-x-3">
                <Button 
                  onClick={handleUpdate}
                  disabled={isUpdating || isGenerating}
                  variant="outline"
                  className="border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  {isUpdating ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={handleUpdateAndGenerate}
                  disabled={isUpdating || isGenerating}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 dark:from-blue-500 dark:to-purple-500 dark:hover:from-blue-600 dark:hover:to-purple-600 text-white"
                >
                  {isGenerating ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Save & Generate
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
} 